import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ImagePlus, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { uploadPhoto, createPost, formatApiErrorDetail } from "@/lib/api";

const CreatePostModal = ({ open, onClose, onCreated }) => {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | done
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const reset = () => {
    setText("");
    setFile(null);
    setPreview(null);
    setStatus("idle");
    setError("");
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() || status === "loading") return;
    setStatus("loading");
    setError("");
    try {
      let image_path = null;
      if (file) {
        const up = await uploadPhoto(file);
        image_path = up.path;
      }
      const post = await createPost({ text: text.trim(), image_path });
      onCreated(post);
      setStatus("done");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setStatus("idle");
    }
  };

  const close = () => {
    reset();
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] grid place-items-center bg-slate-900/50 p-4 backdrop-blur-sm"
          onClick={close}
          data-testid="create-post-overlay"
        >
          <motion.div
            initial={{ scale: 0.9, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.94, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg rounded-3xl bg-white p-8 shadow-2xl"
            data-testid="create-post-modal"
          >
            <button
              onClick={close}
              data-testid="create-post-close"
              aria-label="Close"
              className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-[#F8F5EC] text-slate-500 transition-transform hover:scale-110"
            >
              <X className="h-4 w-4" />
            </button>

            {status === "done" ? (
              <div className="py-6 text-center" data-testid="create-post-success">
                <CheckCircle2 className="mx-auto h-12 w-12 text-[#16A34A]" />
                <h3 className="mt-4 font-display text-2xl font-bold text-slate-900">Sent for review!</h3>
                <p className="mx-auto mt-2 max-w-sm text-sm text-slate-600 leading-relaxed">
                  Every post and photo is checked by our moderation team before it appears
                  publicly. You can see it on your profile while it&apos;s under review.
                </p>
                <button
                  onClick={close}
                  data-testid="create-post-done-btn"
                  className="mt-6 rounded-full bg-slate-900 px-8 py-3 font-display text-base font-semibold text-white"
                >
                  Lovely
                </button>
              </div>
            ) : (
              <>
                <h3 className="font-display text-2xl font-bold text-slate-900">Share a moment</h3>
                <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                  <ShieldCheck className="h-3.5 w-3.5 text-[#16A34A]" />
                  Reviewed by moderators before going public
                </p>
                <form onSubmit={submit} className="mt-5 grid gap-4" data-testid="create-post-form">
                  <textarea
                    required
                    rows={4}
                    maxLength={1000}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    data-testid="create-post-text"
                    placeholder="We tried today's Noah activity! The kids loved…"
                    className="w-full resize-none rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-base outline-none focus:border-[#FFC107] focus:bg-white"
                  />
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={pickFile}
                    className="hidden"
                    data-testid="create-post-file-input"
                  />
                  {preview ? (
                    <div className="relative overflow-hidden rounded-2xl">
                      <img src={preview} alt="Upload preview" className="max-h-56 w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => { setFile(null); setPreview(null); }}
                        data-testid="create-post-remove-photo"
                        className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-black/60 text-white"
                        aria-label="Remove photo"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      data-testid="create-post-add-photo"
                      className="flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#E2DDD0] py-6 text-sm font-bold text-slate-500 transition-colors hover:border-[#FFC107] hover:text-slate-700"
                    >
                      <ImagePlus className="h-5 w-5" />
                      Add a photo (optional)
                    </button>
                  )}
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Please don&apos;t include children&apos;s full names, school details or
                    location info. Photo metadata is stripped on upload.
                  </p>
                  {error && (
                    <p className="rounded-2xl bg-[#FFE5E5] px-4 py-3 text-sm font-semibold text-[#DC2626]" data-testid="create-post-error">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={status === "loading" || !text.trim()}
                    data-testid="create-post-submit"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFC107] py-3.5 font-display text-base font-semibold text-slate-900 transition-all hover:-translate-y-0.5 disabled:opacity-50"
                  >
                    {status === "loading" && <Loader2 className="h-4 w-4 animate-spin" />}
                    {status === "loading" ? "Sharing…" : "Share with the community"}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CreatePostModal;
